import React, { useState, useEffect, useRef } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon, ExclamationTriangleIcon, WifiIcon, SignalSlashIcon } from "@heroicons/react/20/solid";

import { getAgentForLLM, getSelectedLLM, setSelectedLLM, type LLMId } from "cindra/MastraClient";
import { getAuthToken } from "services/AccountServices";
import { useMCP } from "mcp/hooks/useMCP";
import { useMCPAuth } from "mcp/hooks/useMCPAuth";
import { CindraPatientInfoPanel } from "cindra/CindraPatientInfoPanel";
import { parsePatientInfoMarkdown } from "cindra/CindraPatientInfoUtils";
import { CindraChatMessageContent } from "cindra/CindraChatMessageContent";

// Debug logging flag
const ENABLE_DEBUG_LOGS = false;

interface Message {
    id: string;
    role: "user" | "assistant" | "system";
    content: string;
    timestamp: Date;
    isError?: boolean;
    errorType?: "api_key" | "network" | "server" | "auth" | "unknown";
    statusMessage?: string;
    isConnectivityStatus?: boolean;
    patientInfoMarkdown?: string;  // Markdown from get_patient_information tool
    patientName?: string;          // Patient name from get_patient_information tool
}

interface LLMProvider {
    id: string;
    name: string;
    model: string;
    provider: "anthropic" | "openai" | "google";
}

const LLM_PROVIDERS: LLMProvider[] = [
    {
        id: "gpt-5",
        name: "GPT-5",
        model: "gpt-4o",
        provider: "openai"
    },
    {
        id: "claude-4-sonnet",
        name: "Claude 4 Sonnet",
        model: "claude-4-sonnet-20250514",
        provider: "anthropic"
    },
    {
        id: "gemini-2.5-pro",
        name: "Gemini 2.5 Pro",
        model: "gemini-2.0-pro",
        provider: "google"
    }
];

export function CindraChat() {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);

    // Initialize selectedLLM from cookie on mount
    const [selectedLLM, setSelectedLLMState] = useState<LLMProvider>(() => {
        const savedLLMId = getSelectedLLM();
        return LLM_PROVIDERS.find(p => p.id === savedLLMId) || LLM_PROVIDERS[0];
    });
    const mcpState = useMCP();
    const mcpAuth = useMCPAuth();

    // Track previous connection state to detect changes
    const prevConnectionState = useRef<{
        isConnected: boolean;
        hasAuthToken: boolean;
        tokenValidated: boolean | null;
        reconnectAttempts: number;
    }>({
        isConnected: mcpState.isCindraConnected,
        hasAuthToken: mcpAuth.hasAuthToken,
        tokenValidated: mcpAuth.tokenValidated,
        reconnectAttempts: mcpState.reconnectAttempts
    });

    // Set up initial system message with authentication context
    useEffect(() => {
        if (mcpState.isCindraConnected && messages.length === 0) {
            const createInitialMessage = async () => {
                // // Wait a moment for token validation to complete
                // await new Promise(resolve => setTimeout(resolve, 1500));
                setMessages([{
                    id: "0",
                    role: "assistant",
                    content: `Hello! I'm Cindra, your Karuna AI assistant. How can I help you today?`,
                    timestamp: new Date()
                }]);
            };

            void createInitialMessage();
        }
    }, [mcpState.isCindraConnected, mcpState.tools.length, mcpState.resources.length, messages.length, mcpAuth.tokenValidated, mcpAuth.validatedUser, mcpAuth.hasAuthToken]);

    // Monitor MCP connectivity changes and add status messages to chat
    useEffect(() => {
        const prevState = prevConnectionState.current;
        const currentState = {
            isConnected: mcpState.isCindraConnected,
            hasAuthToken: mcpAuth.hasAuthToken,
            tokenValidated: mcpAuth.tokenValidated,
            reconnectAttempts: mcpState.reconnectAttempts
        };

        // Connection lost
        if (prevState.isConnected && !currentState.isConnected && messages.length > 0) {
            const disconnectMessage: Message = {
                id: `mcp-disconnect-${Date.now()}`,
                role: "system",
                content: "🔌 **Connection Lost** - Lost connection to Cindra MCP server. Attempting to reconnect...",
                timestamp: new Date(),
                isConnectivityStatus: true
            };
            setMessages(prev => [...prev, disconnectMessage]);
        }

        // Connection restored - remove any reconnecting messages and add restoration message
        if (!prevState.isConnected && currentState.isConnected && messages.length > 0) {
            setMessages(prev => {
                // Remove any existing reconnecting messages
                const filteredMessages = prev.filter(msg =>
                    !(msg.isConnectivityStatus && (
                        msg.content.includes("Connection Lost") ||
                        msg.content.includes("Reconnecting")
                    ))
                );

                // Add restoration message only if we had disconnection messages
                const hadDisconnectionMessages = prev.some(msg =>
                        msg.isConnectivityStatus && (
                            msg.content.includes("Connection Lost") ||
                            msg.content.includes("Reconnecting")
                        )
                );

                if (hadDisconnectionMessages) {
                    const reconnectMessage: Message = {
                        id: `mcp-reconnect-${Date.now()}`,
                        role: "system",
                        content: "✅ **Connection Restored** - Successfully reconnected to Cindra.",
                        timestamp: new Date(),
                        isConnectivityStatus: true
                    };
                    return [...filteredMessages, reconnectMessage];
                }

                return filteredMessages;
            });
        }

        // Update or add reconnection attempt message (consolidate into single message)
        if (!currentState.isConnected && currentState.reconnectAttempts > 0 && messages.length > 0) {
            setMessages(prev => {
                // Remove any existing reconnecting message
                const filteredMessages = prev.filter(msg =>
                    !(msg.isConnectivityStatus && msg.content.includes("Reconnecting"))
                );

                // Add updated reconnecting message
                const attemptMessage: Message = {
                    id: `mcp-reconnecting`, // Fixed ID so it can be found and replaced
                    role: "system",
                    content: `🔄 **Reconnecting** - Attempt ${currentState.reconnectAttempts}. Cindra is temporarily unavailable.`,
                    timestamp: new Date(),
                    isConnectivityStatus: true
                };

                return [...filteredMessages, attemptMessage];
            });
        }

        // Update previous state
        prevConnectionState.current = currentState;
    }, [mcpState.isCindraConnected, mcpState.reconnectAttempts, mcpAuth.hasAuthToken, mcpAuth.tokenValidated, messages.length]);

    // Helper function to extract status message from response
    const extractStatusMessage = (content: string): { statusMessage?: string; mainContent: string } => {
        // Pattern to detect status messages like "I'll retrieve the information for patient xxx for you."
        const statusPatterns = [
            /^(I'll\s+.+?for\s+you\.)\s*/i,
            /^(Let me\s+.+?for\s+you\.)\s*/i,
            /^(I'm\s+retrieving\s+.+?\.)\s*/i,
            /^(Getting\s+.+?\.)\s*/i,
            /^(Searching\s+for\s+.+?\.)\s*/i,
            /^(Looking\s+up\s+.+?\.)\s*/i
        ];

        for (const pattern of statusPatterns) {
            const match = content.match(pattern);
            if (match) {
                const statusMessage = match[1];
                const mainContent = content.substring(match[0].length).trim();

                // Only separate if there's significant content after the status message
                if (mainContent.length > 50) {
                    return { statusMessage, mainContent };
                }
            }
        }

        return { mainContent: content };
    };

    // Helper function to categorize and format errors
    const categorizeError = (error: Error | unknown): { type: Message["errorType"], message: string } => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const lowerMessage = errorMessage.toLowerCase();

        // Check for authentication errors first
        if (lowerMessage.includes("no authentication token") ||
            lowerMessage.includes("token") && lowerMessage.includes("authentication")) {
            return {
                type: "auth",
                message: "🔑 **Authentication Error**: No valid authentication token available. Please ensure you are logged into the main application."
            };
        }

        // Check for specific AI SDK error types
        if (errorMessage.includes("AI_LoadAPIKeyError") || errorMessage.includes("LoadAPIKeyError")) {
            return {
                type: "api_key",
                message: "🔑 **API Key Missing**: The AI model API key is not configured. Please set your environment variables and restart the Mastra development server."
            };
        }

        // Check for API key issues (broader check)
        if (lowerMessage.includes("api key") || lowerMessage.includes("unauthorized") && !lowerMessage.includes("token")) {
            return {
                type: "api_key",
                message: "❌ **API Key Error**: The selected model's API key is not configured or is invalid. Please check your environment variables."
            };
        }

        // Check for network issues
        if (lowerMessage.includes("network") || lowerMessage.includes("fetch") || lowerMessage.includes("connection") || lowerMessage.includes("timeout")) {
            return {
                type: "network",
                message: "🌐 **Network Error**: Unable to connect to the AI service. Please check your internet connection and try again."
            };
        }

        // Check for server issues
        if (lowerMessage.includes("server") || lowerMessage.includes("500") || lowerMessage.includes("503")) {
            return {
                type: "server",
                message: "🔧 **Server Error**: The AI service is currently unavailable. Please try again in a moment."
            };
        }

        // Generic error
        return {
            type: "unknown",
            message: `⚠️ **Error**: ${errorMessage}`
        };
    };

    const sendMessage = async () => {
        if (!input.trim() || loading) return;

        // Check MCP connectivity before sending
        if (!mcpState.isCindraConnected) {
            const connectivityWarning: Message = {
                id: `connectivity-warning-${Date.now()}`,
                role: "system",
                content: "⚠️ **No Connection** - Cindra MCP server is not available. Please wait for connection to be restored before sending messages.",
                timestamp: new Date(),
                isConnectivityStatus: true
            };
            setMessages(prev => [...prev, connectivityWarning]);
            return;
        }

        const userMessage: Message = {
            id: Date.now().toString(),
            role: "user",
            content: input,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput("");
        setLoading(true);

        try {
            const selectedAgent = getAgentForLLM(selectedLLM.id as LLMId);
            let result: { text?: string; object?: any; toolResults?: any[] } | null;

            // Build conversation messages for the Mastra agent (excluding error messages)
            const conversationMessages = messages
                .filter(msg => !msg.isError)
                .map(msg => ({
                    role: msg.role as "user" | "assistant",
                    content: msg.content
                }));

            // Enhance analysis requests with formatting instructions
            let enhancedUserContent = userMessage.content;
//             if (userMessage.content.toLowerCase().includes("analyze") ||
//                 userMessage.content.toLowerCase().includes("report") ||
//                 userMessage.content.toLowerCase().includes("assessment")) {
//                 enhancedUserContent += `\n\nPlease provide a comprehensive, professionally formatted analysis with:
// - Clear section headers using markdown
// - Executive summary
// - Specific metrics and timelines
// - Clinical significance
// - Prognosis and recommendations
// - Professional medical report formatting`;
//             }

            // Add user context if we have validated user info
            let contextualContent = enhancedUserContent;
            if (mcpAuth.validatedUser) {
                contextualContent = `[auth_token: ${getAuthToken()}, user: ${mcpAuth.validatedUser.email}${mcpAuth.validatedUser.first_name ? ` (${mcpAuth.validatedUser.first_name} ${mcpAuth.validatedUser.last_name})` : ''}]\n\n${enhancedUserContent}`;
            }

            // Prepare messages for the agent
            const allMessages = [
                ...conversationMessages,
                { role: "user", content: contextualContent }
            ];

            // Try generateVNext first for V2 model compatibility
            if (typeof selectedAgent.generateVNext === "function") {
                // Convert messages to string format as shown in your previous working version
                const conversationString = allMessages
                    .map(msg => `${msg.role}: ${msg.content}`)
                    .join("\n");

                result = await selectedAgent.generateVNext({
                    messages: conversationString,
                    threadId: "main-conversation",
                    resourceId: "current-user",
                });
            }
            else {
                result = await selectedAgent.generate({
                    messages: conversationMessages,
                    threadId: "main-conversation",
                    resourceId: "current-user"
                });
            }

            if (ENABLE_DEBUG_LOGS) {
                console.log("[CindraChat] Agent response:", result);
                console.log("[CindraChat] Agent response toolResults:", result?.toolResults);
            }

            if (result?.text) {
                let { statusMessage, mainContent } = extractStatusMessage(result.text);

                // Check if the agent used the Cindra MCP get_patient_information tool
                let patientInfoMarkdown: string | undefined;
                let patientName: string | undefined;

                // Access tool results from the agent response
                if (result?.toolResults && Array.isArray(result.toolResults)) {
                    if (ENABLE_DEBUG_LOGS) console.log('[CindraChat] Processing', result.toolResults.length, 'tool results');
                    for (const toolResult of result.toolResults) {
                        if (toolResult?.payload?.toolName === 'cindramcpserver_get_patient_information') {
                            if (ENABLE_DEBUG_LOGS) console.log('[CindraChat] Found get_patient_information tool result:', toolResult);

                            // Extract markdown from tool result payload
                            const markdown = toolResult?.payload?.result?.structuredContent?.markdown;
                            if (markdown) {
                                patientInfoMarkdown = markdown;
                                // Try to extract patient name from tool arguments
                                if (toolResult?.payload?.args?.full_name) {
                                    patientName = toolResult.payload.args.full_name;
                                }
                                if (ENABLE_DEBUG_LOGS) {
                                    console.log('[CindraChat] Extracted patient info markdown for:', patientName);
                                    console.log('[CindraChat] Patient info markdown length:', patientInfoMarkdown.length);
                                }

                                // Since we'll show the patient info in a structured panel, remove all the markdown from the text response
                                // Keep only the first sentence or two (introductory text before the markdown starts)
                                const firstSectionMatch = mainContent.match(/^([^#\-\*]+?)(?=\n\s*[-#\*]|\n\n[-#\*])/s);
                                if (firstSectionMatch) {
                                    mainContent = firstSectionMatch[1].trim();
                                } else {
                                    // If no clear intro text, just use a simple response
                                    mainContent = `Here's the information for ${patientName || 'the patient'}:`;
                                }
                            }
                        }
                    }
                }

                const assistantMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: "assistant",
                    content: mainContent,
                    timestamp: new Date(),
                    statusMessage,
                    patientInfoMarkdown,
                    patientName
                };

                setMessages(prev => [...prev, assistantMessage]);
            }
            else {
                throw new Error("No response generated from agent");
            }

        }
        catch (agentError) {
            console.error("[CindraChat] Agent generation error:", agentError);
            const { type, message } = categorizeError(agentError);

            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: message,
                timestamp: new Date(),
                isError: true,
                errorType: type
            };

            setMessages(prev => [...prev, errorMessage]);
        }
        finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void sendMessage();
        }
    };

    const getConnectionStatus = () => {
        if (!mcpState.isCindraConnected) {
            if (mcpState.reconnectAttempts > 0) {
                return { color: "text-yellow-600", text: `Reconnecting (${mcpState.reconnectAttempts})`, icon: "reconnecting" };
            }
            return { color: "text-red-600", text: "Disconnected", icon: "disconnected" };
        }

        if (mcpAuth.tokenValidated === true) {
            return { color: "text-green-600", text: "Connected", icon: "connected" };
        } else if (mcpAuth.tokenValidated === false) {
            return { color: "text-yellow-600", text: "Connected (Auth Failed)", icon: "connected" };
        } else if (mcpAuth.hasAuthToken) {
            return { color: "text-yellow-600", text: "Connected (Validating...)", icon: "connected" };
        } else {
            return { color: "text-yellow-600", text: "Connected (No Token)", icon: "connected" };
        }
    };

    const connectionStatus = getConnectionStatus();

    return (
        <div className="flex flex-col h-full bg-white border border-gray-300 rounded-lg shadow">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900">Cindra Chat</h3>
                    <div className="flex items-center space-x-2">
                        {/* Connection status with icon */}
                        {connectionStatus.icon === "connected" && (
                            <WifiIcon className={`h-4 w-4 ${connectionStatus.color}`} />
                        )}
                        {connectionStatus.icon === "disconnected" && (
                            <SignalSlashIcon className="h-4 w-4 text-red-500" />
                        )}
                        {connectionStatus.icon === "reconnecting" && (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-500"></div>
                        )}
                        <span className={`text-xs font-medium ${connectionStatus.color}`}>
                            {connectionStatus.text}
                        </span>
                    </div>
                </div>

                {/* LLM Selector */}
                <Listbox value={selectedLLM} onChange={(llm) => {
                    setSelectedLLMState(llm);
                    setSelectedLLM(llm.id as LLMId); // Save to cookie
                }}>
                    <div className="relative">
                        <ListboxButton className="relative w-48 cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left border border-gray-300 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm">
                            <span className="block truncate">{selectedLLM.name}</span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </span>
                        </ListboxButton>

                        <ListboxOptions className="absolute right-0 z-10 mt-1 max-h-60 w-48 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {LLM_PROVIDERS.map((provider) => (
                                <ListboxOption
                                    key={provider.id}
                                    className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                            active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                                        }`
                                    }
                                    value={provider}
                                >
                                    {({ selected }) => (
                                        <>
                                            <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                                {provider.name}
                                            </span>
                                            {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </ListboxOption>
                            ))}
                        </ListboxOptions>
                    </div>
                </Listbox>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => (
                    <div key={message.id}>
                        {/* Status Message (if present) */}
                        {message.statusMessage && !message.isConnectivityStatus && (
                            <div className="flex justify-start mb-2">
                                <div className="max-w-3xl px-3 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 mr-4 text-sm italic">
                                    <div className="flex items-center">
                                        <div className="animate-pulse h-2 w-2 bg-blue-400 rounded-full mr-2"></div>
                                        <span className="select-text">{message.statusMessage}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Connectivity Status Messages - Special Styling */}
                        {message.isConnectivityStatus ? (
                            <div className="flex justify-center mb-4">
                                <div className="px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-600 text-xs flex items-center space-x-2 shadow-sm">
                                    {message.content.includes("Connection Lost") && (
                                        <SignalSlashIcon className="h-3 w-3 text-red-500" />
                                    )}
                                    {message.content.includes("Connection Restored") && (
                                        <WifiIcon className="h-3 w-3 text-green-500" />
                                    )}
                                    {message.content.includes("Reconnecting") && (
                                        <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>
                                    )}
                                    <span>{message.content.replace(/\*\*/g, '').replace(/🔌|✅|🔄|⚠️/g, '').trim()}</span>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-gray-400">{message.timestamp.toLocaleTimeString()}</span>
                                </div>
                            </div>
                        ) : (
                            /* Regular Message */
                            <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-3xl rounded-lg px-4 py-2 ${
                                    message.role === "user"
                                        ? "bg-blue-600 text-white ml-4"
                                        : message.isError
                                            ? "bg-red-50 text-red-800 border border-red-200 mr-4"
                                            : "bg-gray-100 text-gray-900 mr-4"
                                }`}>
                                    {message.isError && message.errorType === "auth" && (
                                        <div className="flex items-center mb-2">
                                            <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mr-2" />
                                            <span className="text-xs font-medium text-red-600">Authentication Issue</span>
                                        </div>
                                    )}
                                    {/* Render message content with code block support */}
                                    <CindraChatMessageContent
                                        content={message.content}
                                        isUserMessage={message.role === "user"}
                                    />
                                    <div className="mt-2 text-xs opacity-70 select-text">
                                        {message.timestamp.toLocaleTimeString()}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Patient Info Panel - inline with chat when get_patient_information was called */}
                        {message.patientInfoMarkdown && !message.isError && (() => {
                            const sections = parsePatientInfoMarkdown(message.patientInfoMarkdown);
                            return (
                                <div className="flex justify-start mt-2 mr-4">
                                    <CindraPatientInfoPanel
                                        sections={sections}
                                        patientName={message.patientName}
                                        showHeader={true}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                ))}
                {loading && (
                    <div className="flex justify-start">
                        <div className="bg-gray-100 text-gray-900 rounded-lg px-4 py-2 mr-4">
                            <div className="flex items-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <span className="select-text">Cindra is thinking...</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Input */}
            <div className="border-t border-gray-200 p-4">
                <div className="flex space-x-2">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder={mcpState.isCindraConnected ? "Ask Cindra about your healthcare data..." : "Waiting for Cindra connection..."}
                        className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                        rows={1}
                        disabled={loading || !mcpState.isCindraConnected}
                    />
                    <button
                        onClick={() => void sendMessage()}
                        disabled={loading || !input.trim() || !mcpState.isCindraConnected}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Send
                    </button>
                </div>
                {/* Status indicators */}
                <div className="mt-4 flex items-center justify-end text-xs text-gray-400">
                    <div className="flex items-center space-x-1">
                        Press Enter to send, Shift+Enter for new line
                    </div>
                </div>
            </div>
        </div>
    );
}
